import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Camera, MapPin, ExternalLink, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Renderiza a foto de evidência (buscada como blob, com Authorization header),
 * o horário do upload e o link do GPS. Somente exibe se a entrega tiver foto_evidencia_path.
 */
export function EvidenceViewer({ entrega }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const hasFoto = !!entrega?.foto_evidencia_path;
  const hasGPS = entrega?.latitude != null && entrega?.longitude != null;

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    if (!hasFoto || !entrega?.id) { setBlobUrl(null); return; }
    setLoading(true); setErr(false);
    api.get(`/entregas/${entrega.id}/evidencia`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      })
      .catch(() => { if (!cancelled) setErr(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [entrega?.id, hasFoto]);

  if (!hasFoto && !hasGPS) return null;

  const fmtAt = entrega?.foto_evidencia_at
    ? new Date(entrega.foto_evidencia_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
    : null;

  return (
    <div className="border border-orange-100 rounded-lg p-3 space-y-2 bg-orange-50/40" data-testid="evidence-viewer">
      <div className="flex items-center gap-2 text-sm font-bold text-orange-800">
        <Camera className="w-4 h-4" />Evidência da entrega
      </div>

      {hasFoto ? (
        <div>
          {loading && <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">Carregando foto…</div>}
          {err && (
            <div className="h-56 flex flex-col items-center justify-center text-sm text-rose-600 gap-1">
              <ImageOff className="w-6 h-6" />Falha ao carregar imagem
            </div>
          )}
          {blobUrl && !err && (
            <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img src={blobUrl} alt="Evidência da entrega" className="w-full max-h-72 object-contain bg-black/5 rounded" data-testid="evidence-img" />
            </a>
          )}
          {fmtAt && <div className="text-xs text-muted-foreground mt-1">Enviada em {fmtAt}</div>}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">Sem foto (entrega criada antes da funcionalidade)</div>
      )}

      {hasGPS && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <MapPin className="w-4 h-4 text-orange-600" />
            <span className="mono text-xs">{entrega.latitude.toFixed(6)}, {entrega.longitude.toFixed(6)}</span>
          </div>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-100 h-7"
            data-testid="evidence-gps-link"
          >
            <a href={`https://www.google.com/maps?q=${entrega.latitude},${entrega.longitude}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3 h-3 mr-1" />Abrir no Maps
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}
